/* REALM — theme behaviour.
   The prototype swapped views in JS state; in Shopify these are real page
   loads, so the only JS the design needs is the mobile nav, scroll reveals,
   and keeping the header cart count in sync with Dawn's AJAX cart. */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- nav */
  function initNav() {
    var burger = document.querySelector('[data-realm-burger]');
    var nav = document.querySelector('[data-realm-nav]');
    if (!burger || !nav) return;

    function setOpen(open) {
      nav.classList.toggle('is-open', open);
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }

    burger.addEventListener('click', function () {
      setOpen(!nav.classList.contains('is-open'));
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && nav.classList.contains('is-open')) {
        setOpen(false);
        burger.focus();
      }
    });

    // Reset when the layout crosses back to the desktop breakpoint.
    var desktop = window.matchMedia('(min-width: 1100px)');
    var onChange = function (event) {
      if (event.matches) setOpen(false);
    };
    if (desktop.addEventListener) desktop.addEventListener('change', onChange);
    else desktop.addListener(onChange);
  }

  /* ------------------------------------------------------------ reveals */
  // Content must NEVER be permanently invisible. IntersectionObserver does not
  // fire in every context (backgrounded tabs, prerenderers, some crawlers), so
  // the observer is treated as an enhancement with three independent backstops:
  // an in-view pass on load, a throttled scroll pass, and a final timeout.
  function initReveals() {
    var targets = [].slice.call(document.querySelectorAll('[data-realm-reveal]'));
    if (!targets.length) return;

    // Health signal for the observer itself. IntersectionObserver always delivers an
    // initial entry for every observed target, intersecting or not — so if this is
    // still false after a moment, the observer is not running in this context.
    var observerAlive = false;

    function show(el) {
      el.classList.add('realm-rise');
    }

    function showAll() {
      targets.forEach(show);
    }

    // No observer, or the visitor asked for less motion: leave everything visible.
    if (
      !('IntersectionObserver' in window) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      return;
    }

    // Arm the page. The hidden states in realm.css apply ONLY while this class is
    // present, so if this script never runs the site simply renders visible.
    document.body.classList.add('js-reveal');

    // `realm-ready` releases the hero lockup. rAF is the nice path, but it does not
    // run in throttled or backgrounded contexts — timers do, so guard it. Without
    // this the hero headline could stay at opacity 0.
    function markReady() {
      document.body.classList.add('realm-ready');
    }
    window.requestAnimationFrame(markReady);
    window.setTimeout(markReady, 400);

    var observer = new IntersectionObserver(
      function (entries) {
        observerAlive = true;
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          show(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    targets.forEach(function (el) {
      observer.observe(el);
    });

    // Backstop 1 + 2: reveal whatever is already on screen, at load and on
    // scroll, without waiting for the observer.
    function sweep() {
      var h = window.innerHeight || document.documentElement.clientHeight;
      targets.forEach(function (el) {
        if (el.classList.contains('realm-rise')) return;
        var r = el.getBoundingClientRect();
        if (r.top < h && r.bottom > 0) {
          show(el);
          observer.unobserve(el);
        }
      });
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        sweep();
      });
    }

    window.requestAnimationFrame(sweep);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    window.addEventListener('load', sweep);

    // Backstop 3: if the observer never reported in, it is not running here. Reveal
    // everything rather than leave the page part-invisible.
    //
    // This deliberately checks the OBSERVER rather than "did anything reveal" — an
    // element already on screen at load gets revealed by the sweep above, and using
    // that as the signal would switch this backstop off while the rest of the page
    // stayed hidden.
    window.setTimeout(function () {
      if (!observerAlive) showAll();
    }, 1800);
  }

  /* --------------------------------------------------------- cart count */
  // Dawn re-renders `cart-icon-bubble` after every AJAX cart update. The REALM
  // header shows a text count instead, so mirror the number across.
  function initCartCount() {
    var node = document.querySelector('[data-realm-cart-count]');
    if (!node) return;

    function refresh() {
      fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (cart) {
          if (cart) node.textContent = cart.item_count;
        })
        .catch(function () {
          /* leave the server-rendered count in place */
        });
    }

    document.addEventListener('cart:refresh', refresh);
    // Dawn dispatches this on its publish/subscribe bridge; also catch the
    // native events fired by the cart drawer and quantity inputs.
    ['cart:updated', 'cart-drawer:open'].forEach(function (name) {
      document.addEventListener(name, refresh);
    });

    if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, refresh);
    }
  }

  function init() {
    initNav();
    initReveals();
    initCartCount();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* --------------------------------------------------------------------------
   Buy box · purchase type
   Shopify records the chosen plan from the `selling_plan` field posted with the
   add-to-cart form; the subscription app reads it from the resulting contract.
   The input stays disabled while "one-time" is selected so it is never posted.
   -------------------------------------------------------------------------- */
(function () {
  var box = document.querySelector('[data-realm-buy]');
  if (!box) return;

  var planInput = box.querySelector('[data-realm-plan-input]');
  var panel = box.querySelector('[data-realm-panel]');
  var modes = box.querySelectorAll('[data-realm-mode]');
  var cadences = box.querySelectorAll('[data-realm-cadence]');
  var subPrice = box.querySelector('[data-realm-sub-price]');
  var form = box.closest('form');
  var cta = form ? form.querySelector('[data-realm-cta]') : null;
  var ctaOnce = cta ? cta.textContent.trim() : '';
  var ctaSub = box.getAttribute('data-subscribe-cta') || ctaOnce;
  var subscribing = false;

  function activeCadence() {
    for (var i = 0; i < cadences.length; i++) {
      if (cadences[i].classList.contains('is-active')) return cadences[i];
    }
    return cadences[0];
  }

  function sync() {
    var chosen = activeCadence();

    for (var i = 0; i < modes.length; i++) {
      var on = (modes[i].getAttribute('data-realm-mode') === 'subscribe') === subscribing;
      modes[i].classList.toggle('is-active', on);
      modes[i].setAttribute('aria-checked', on ? 'true' : 'false');
    }

    if (panel) panel.hidden = !subscribing;

    if (planInput) {
      if (subscribing && chosen) {
        planInput.value = chosen.getAttribute('data-plan');
        planInput.disabled = false;
      } else {
        planInput.value = '';
        planInput.disabled = true;
      }
    }

    if (subPrice && chosen) {
      subPrice.textContent = chosen.getAttribute('data-price-formatted');
    }
    if (cta) cta.textContent = subscribing ? ctaSub : ctaOnce;
  }

  for (var m = 0; m < modes.length; m++) {
    modes[m].addEventListener('click', function () {
      subscribing = this.getAttribute('data-realm-mode') === 'subscribe';
      sync();
    });
  }

  for (var c = 0; c < cadences.length; c++) {
    cadences[c].addEventListener('click', function () {
      for (var i = 0; i < cadences.length; i++) {
        cadences[i].classList.remove('is-active');
        cadences[i].setAttribute('aria-checked', 'false');
      }
      this.classList.add('is-active');
      this.setAttribute('aria-checked', 'true');
      sync();
    });
  }

  sync();
})();

/* --------------------------------------------------------------------------
   Subscription portal
   Smartrr serves the portal through an app proxy, so it renders inside this
   theme's layout but its markup is not ours to author. Marking the route lets
   realm.css restyle it without leaking those rules onto the rest of the site.
   -------------------------------------------------------------------------- */
(function () {
  if (document.body && /^\/a\/subscriptions(\/|$)/.test(window.location.pathname)) {
    document.body.setAttribute('data-realm-portal', '');
  }
})();
